import { useMemo } from "react";

// Picks image resolution based on network speed + device.
// Local /products/*.webp images get 320px / 480px / original variants;
// uploaded images (/api/files/*.webp) get the variants stored at upload time;
// other remote images pass through untouched.
function buildSources(src) {
  if (!src || !src.endsWith(".webp")) return null;
  if (src.includes("-320.") || src.includes("-480.")) return null;
  if (src.startsWith("/products/") || src.startsWith("/api/files/")) {
    const base = src.slice(0, -5);
    return {
      small: `${base}-320.webp`,
      medium: `${base}-480.webp`,
      original: src,
    };
  }
  return null;
}

function slowConnection() {
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!conn) return false;
  if (conn.saveData) return true;
  return ["slow-2g", "2g", "3g"].includes(conn.effectiveType);
}

export default function SmartImage({
  src,
  alt = "",
  className = "",
  sizes = "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw",
  eager = false,
  ...rest
}) {
  const sources = useMemo(() => buildSources(src), [src]);
  const slow = useMemo(slowConnection, []);

  if (!sources) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        {...rest}
      />
    );
  }

  // Slow or data-saver connections: serve the smallest variant only.
  if (slow) {
    return (
      <img
        src={sources.small}
        alt={alt}
        className={className}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        {...rest}
      />
    );
  }

  const originalWidth = src.startsWith("/api/files/") ? 1600 : 671;
  return (
    <img
      src={sources.medium}
      srcSet={`${sources.small} 320w, ${sources.medium} 480w, ${sources.original} ${originalWidth}w`}
      sizes={sizes}
      alt={alt}
      className={className}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      {...rest}
    />
  );
}
